/**
 * SeriesTableV2 — Series list, ported from the prototype's
 * `.screen-series` (cms-redesign-mockup.html ~line 6364+). Visually
 * a sibling to AudiobookTableV2 — same `.ab-card` recipe (cover +
 * title + meta line) with one extra adornment: a category eyebrow
 * above the title (`.ab-eyebrow`, prototype 1341–1346) — and inline
 * "New" state badges when `series_is_new` is set.
 *
 * Layout:
 *   • Toolbar — status SegmentedControl (All / Active / Inactive with
 *     counts) + Cards/Table view toggle on the right.
 *   • Card grid (default) — `.audiobook-grid` repeat(auto-fill,
 *     minmax(220px,1fr)) of `.ab-card`s.
 *   • Compact LibTable — alternate dense layout, same shape as the
 *     Audiobooks table view.
 *
 * Data: reuses the v1 `fetchAllSeries` action (GET /series, legacy
 * `{objects, count}` envelope). After #562 the response already
 * includes `series_image`, `authors[]`, `series_is_new`, and
 * `total_sessions`, so no API work is needed.
 *
 * No drag-to-reorder — series order isn't user-managed (no
 * `series_sort_order` on the model). Status filter runs client-side.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  fetchAllSeries,
  clearAllSeries,
} from '../../../actions/seriesActions';
import {
  LibTable,
  SegmentedControl,
  IconButton,
  StatusEdge,
  cn,
} from '../../redesign';

/* ─── Icons ────────────────────────────────────────────────────────── */

const EditIconSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" />
  </svg>
);

const CardsViewIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
  </svg>
);

const TableViewIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="4" y1="6"  x2="20" y2="6"  />
    <line x1="4" y1="12" x2="20" y2="12" />
    <line x1="4" y1="18" x2="20" y2="18" />
  </svg>
);

/* ─── Helpers ──────────────────────────────────────────────────────── */

function initials(name) {
  if (!name) return '··';
  const words = String(name).trim().split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

const AVATAR_BG = ['#75A293', '#C4715B', '#9DA67E'];
function avatarBgFor(index, author) {
  if (author?.author_image) return undefined;
  return AVATAR_BG[index % AVATAR_BG.length];
}

/* ─── Author cluster (single + multi author aware) ─────────────────── */

function AuthorCluster({ authors }) {
  if (!Array.isArray(authors) || authors.length === 0) {
    return <span className="text-text-muted">—</span>;
  }

  const showCount = authors.length > 1;
  return (
    <span className="inline-flex items-center" style={{ gap: 6 }}>
      <span className="inline-flex" style={{ flexShrink: 0 }}>
        {authors.slice(0, 3).map((a, idx) => (
          <span
            key={a.author_id ?? `${a.author_name}-${idx}`}
            className="grid place-items-center font-condensed font-bold text-white"
            style={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              fontSize: 10.5,
              border: '1.5px solid #FAF7F1',
              marginLeft: idx === 0 ? 0 : -7,
              background: avatarBgFor(idx, a),
              backgroundImage: a.author_image ? `url('${a.author_image}')` : undefined,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
            aria-hidden
          >
            {!a.author_image && initials(a.author_name)}
          </span>
        ))}
      </span>
      {showCount ? (
        <span className="text-text-light" style={{ fontWeight: 500 }}>
          {authors.length} authors
        </span>
      ) : (
        <span className="text-text" style={{ fontWeight: 500 }}>
          {authors[0].author_name}
        </span>
      )}
    </span>
  );
}

/* ─── "New" state badge ─────────────────────────────────────────────── */

function NewBadge() {
  return (
    <span
      className="inline-flex items-center font-condensed font-extrabold uppercase text-white rounded-pill shrink-0"
      style={{
        fontSize: 9.5,
        letterSpacing: '0.08em',
        padding: '2px 7px',
        background: '#E58A6A', /* coral */
        marginTop: 2,
      }}
    >
      New
    </span>
  );
}

/* ─── Card ─────────────────────────────────────────────────────────── */
/*
 *  Mirrors `.ab-card` + adds the `.ab-eyebrow` category line above
 *  the title (prototype 1341–1346: Asap Condensed 10.5px 700,
 *  uppercase teal-green-d).
 */
function SeriesCard({ s }) {
  const active = !!s.series_is_active;
  const sessions = Number(s.total_sessions ?? 0);

  return (
    <Link
      to={`/series/edit/${s.series_id}`}
      className="flex flex-col bg-surface border border-sand overflow-hidden no-underline transition-all duration-200 hover:border-teal-green"
      style={{ borderRadius: 12, color: 'inherit', position: 'relative' }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow =
          '0 10px 24px #2C3E5018, 0 2px 6px #2C3E500C';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = '';
        e.currentTarget.style.boxShadow = '';
      }}
    >
      {/* Square cover (1:1) — matches the audiobook treatment. */}
      <div
        className="bg-cream"
        style={{
          aspectRatio: '1 / 1',
          backgroundImage: s.series_image ? `url('${s.series_image}')` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
          filter: active ? undefined : 'grayscale(40%) brightness(0.95)',
        }}
      >
        <span
          aria-hidden
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            width: 10,
            height: 10,
            borderRadius: '50%',
            boxShadow: '0 0 0 3px #FFFFFFB3',
            background: active ? '#75A293' : '#9CA1A8',
          }}
        />
        {!active && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: '#FAF4EA77',
              pointerEvents: 'none',
            }}
          />
        )}
      </div>

      <div className="flex flex-col" style={{ padding: '14px 16px 16px', gap: 4, flex: 1 }}>
        {/* `.ab-eyebrow` — category title above the series title. */}
        {s.category_title && (
          <div
            className="font-condensed font-bold uppercase"
            style={{
              fontSize: 10.5,
              letterSpacing: '0.08em',
              color: '#5C7E72', /* teal-green-d */
            }}
          >
            {s.category_title}
          </div>
        )}

        {/* `.ab-title-row` — title + optional New badge inline. */}
        <div className="flex items-start" style={{ gap: 6, flexWrap: 'wrap' }}>
          <div
            className={cn(
              'font-bold text-navy',
              !active && 'italic text-text-light',
            )}
            style={{
              fontSize: 15,
              letterSpacing: '-0.005em',
              lineHeight: 1.25,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              flex: '1 1 auto',
              minWidth: 0,
            }}
          >
            {s.series_title || 'Untitled'}
          </div>
          {s.series_is_new && <NewBadge />}
        </div>

        {/* Two-line meta stack: authors first, then sessions count. */}
        <div className="flex flex-col" style={{ marginTop: 'auto', paddingTop: 10, gap: 4 }}>
          <div style={{ fontSize: 13 }}>
            <AuthorCluster authors={s.authors} />
          </div>
          {sessions > 0 && (
            <div className="text-text-light font-mono" style={{ fontSize: 11.5 }}>
              {sessions} session{sessions === 1 ? '' : 's'}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}

/* ─── Table view ───────────────────────────────────────────────────── */

function SeriesTableRows({ items }) {
  return (
    <div style={{ padding: '0 28px 32px' }}>
      <LibTable
        fixed
        className="border border-sand"
        style={{ borderRadius: 12, overflow: 'hidden' }}
      >
        <colgroup>
          <col style={{ width: 22 }} />
          <col style={{ width: 56 }} />
          <col />
          <col style={{ width: '20%' }} />
          <col style={{ width: 80 }} />
          <col style={{ width: '22%' }} />
          <col style={{ width: 60 }} />
        </colgroup>
        <LibTable.Head>
          <tr>
            <LibTable.Th aria-label="Status" style={{ padding: 0 }}> </LibTable.Th>
            <LibTable.Th aria-label="Cover"> </LibTable.Th>
            <LibTable.Th>Series</LibTable.Th>
            <LibTable.Th>Category</LibTable.Th>
            <LibTable.NumTh>Sessions</LibTable.NumTh>
            <LibTable.Th>Author(s)</LibTable.Th>
            <LibTable.Th aria-label="Actions"> </LibTable.Th>
          </tr>
        </LibTable.Head>
        <tbody>
          {items.map((s) => {
            const active = !!s.series_is_active;
            const sessions = Number(s.total_sessions ?? 0);
            return (
              <LibTable.Row key={s.series_id} inactive={!active}>
                <td
                  className="border-b border-line group-data-[inactive=true]:bg-[var(--color-row-inactive)] group-data-[inactive=true]:group-hover:bg-[var(--color-row-inactive-hover)]"
                  style={{ padding: 0, width: 22, position: 'relative' }}
                >
                  <StatusEdge active={active} />
                </td>
                <LibTable.Td>
                  <div
                    className="bg-cream border border-sand"
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 4,
                      backgroundImage: s.series_image
                        ? `url('${s.series_image}')`
                        : undefined,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }}
                  />
                </LibTable.Td>
                <LibTable.Td>
                  <div className="flex items-center min-w-0" style={{ gap: 6 }}>
                    <Link
                      to={`/series/edit/${s.series_id}`}
                      className={cn(
                        'font-semibold no-underline text-navy hover:text-teal-green-dark',
                        'block truncate min-w-0',
                        !active && 'italic text-text-light',
                      )}
                      style={{ fontSize: 13.5 }}
                      title={s.series_title || 'Untitled'}
                    >
                      {s.series_title || 'Untitled'}
                    </Link>
                    {s.series_is_new && <NewBadge />}
                  </div>
                </LibTable.Td>
                <LibTable.Td>
                  <span className="text-text-muted truncate block" style={{ fontSize: 13 }}>
                    {s.category_title || '—'}
                  </span>
                </LibTable.Td>
                <LibTable.NumTd muted={!sessions}>
                  {sessions}
                </LibTable.NumTd>
                <LibTable.Td>
                  <AuthorCluster authors={s.authors} />
                </LibTable.Td>
                <LibTable.Td style={{ textAlign: 'right' }}>
                  <div className="inline-flex opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                    <Link
                      to={`/series/edit/${s.series_id}`}
                      aria-label={`Edit ${s.series_title}`}
                    >
                      <IconButton size="sm" aria-label="Edit">{EditIconSvg}</IconButton>
                    </Link>
                  </div>
                </LibTable.Td>
              </LibTable.Row>
            );
          })}
        </tbody>
      </LibTable>
    </div>
  );
}

/* ─── Main ─────────────────────────────────────────────────────────── */

const STATUS_FILTERS = ['all', 'active', 'inactive'];

export default function SeriesTableV2() {
  const dispatch = useDispatch();
  const series = useSelector((s) => s.series.all_series);

  const [status, setStatus] = useState('all');
  const [view, setView] = useState('grid');

  useEffect(() => {
    dispatch(clearAllSeries());
    dispatch(fetchAllSeries());
  }, [dispatch]);

  const visible = useMemo(() => {
    if (!series) return [];
    if (status === 'all') return series;
    if (status === 'active') return series.filter((s) => s.series_is_active);
    return series.filter((s) => !s.series_is_active);
  }, [series, status]);

  const counts = useMemo(() => {
    if (!series) return { all: 0, active: 0, inactive: 0 };
    return series.reduce(
      (acc, s) => {
        acc.all += 1;
        if (s.series_is_active) acc.active += 1;
        else acc.inactive += 1;
        return acc;
      },
      { all: 0, active: 0, inactive: 0 },
    );
  }, [series]);

  if (!series) {
    return (
      <div
        className="flex items-center justify-center text-text-muted"
        style={{ height: 256 }}
      >
        Loading series…
      </div>
    );
  }

  return (
    <div>
      <div
        className="flex items-center justify-between"
        style={{ padding: '0 28px 16px', gap: 12 }}
      >
        <SegmentedControl
          value={status}
          onChange={setStatus}
          options={STATUS_FILTERS.map((v) => ({
            value: v,
            label: v === 'all' ? 'All' : v === 'active' ? 'Active' : 'Inactive',
            count: counts[v],
          }))}
        />
        <SegmentedControl
          shape="md"
          value={view}
          onChange={setView}
          options={[
            { value: 'grid',  label: 'Cards', icon: CardsViewIcon },
            { value: 'table', label: 'Table', icon: TableViewIcon },
          ]}
        />
      </div>

      {visible.length === 0 ? (
        <div
          className="bg-surface border border-sand text-text-muted italic text-center"
          style={{
            borderRadius: 12,
            padding: '40px 16px',
            fontSize: 13,
            margin: '0 28px 32px',
          }}
        >
          {status === 'all'
            ? 'No series yet — create one to get started.'
            : `No ${status} series.`}
        </div>
      ) : view === 'grid' ? (
        <div
          style={{
            padding: '0 28px 32px',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: 22,
            alignContent: 'start',
            background: 'transparent',
          }}
        >
          {visible.map((s) => (
            <SeriesCard key={s.series_id} s={s} />
          ))}
        </div>
      ) : (
        <SeriesTableRows items={visible} />
      )}
    </div>
  );
}
